import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { openConsentSettings } from '../consent';
import SEO from './SEO';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 8rem 0 4rem;
  background-color: var(--background-color);
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 2.5rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Section = styled.section`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
`;

const SubTitle = styled.h3`
  font-size: 1.1rem;
  margin: 1.25rem 0 0.5rem;
  color: var(--text-color);
`;

const Text = styled.p`
  color: var(--text-color);
  line-height: 1.7;
  margin: 0 0 0.75rem;
`;

const List = styled.ul`
  color: var(--text-color);
  line-height: 1.7;
  margin: 0 0 0.75rem;
  padding-left: 1.25rem;
`;

const ConsentButton = styled.button`
  cursor: pointer;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.3rem;
  font-size: 0.9rem;
  font-weight: 600;
  background: var(--primary-color);
  color: #fff;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }
`;

const Datenschutz: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <SEO
        title="Datenschutzerklärung"
        description="Datenschutzerklärung von Byte Forge: Informationen zur Verarbeitung personenbezogener Daten, Cookies und Ihren Rechten gemäß DSGVO."
        path="/datenschutz"
      />
      <Container>
        <PageTitle>{t('privacy.title_part1')} <span>{t('privacy.title_part2')}</span></PageTitle>

        <Section>
          <SectionTitle>{t('privacy.overview.heading')}</SectionTitle>
          <Text>{t('privacy.overview.text1')}</Text>
          <Text>{t('privacy.overview.text2')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.responsible.heading')}</SectionTitle>
          <Text>{t('privacy.responsible.intro')}</Text>
          <Text>Byte Forge UG (haftungsbeschränkt)</Text>
          <Text>Alte Gärtnerei 4</Text>
          <Text>04425 Taucha</Text>
          <Text>Deutschland</Text>
          <Text>{t('imprint.contact.email_label')}: thomas.gross@byte-forge.it</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.your_rights.heading')}</SectionTitle>
          <Text>{t('privacy.your_rights.intro')}</Text>
          <List>
            <li>{t('privacy.your_rights.item1')}</li>
            <li>{t('privacy.your_rights.item2')}</li>
            <li>{t('privacy.your_rights.item3')}</li>
            <li>{t('privacy.your_rights.item4')}</li>
            <li>{t('privacy.your_rights.item5')}</li>
            <li>{t('privacy.your_rights.item6')}</li>
          </List>
          <Text>{t('privacy.your_rights.complaint')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.hosting.heading')}</SectionTitle>
          <Text>{t('privacy.hosting.text')}</Text>
          <SubTitle>{t('privacy.hosting.provider_heading')}</SubTitle>
          <Text>{t('privacy.hosting.provider_text')}</Text>
          <Text>{t('privacy.hosting.provider_name')}</Text>
          <Text>{t('privacy.hosting.provider_owner')}</Text>
          <Text>{t('privacy.hosting.provider_address1')}</Text>
          <Text>{t('privacy.hosting.provider_address2')}</Text>
          <Text>{t('privacy.hosting.provider_address3')}</Text>
          <Text>{t('privacy.hosting.provider_website')}</Text>
          <Text>{t('privacy.hosting.avv_text')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.server_logs.heading')}</SectionTitle>
          <Text>{t('privacy.server_logs.intro')}</Text>
          <List>
            <li>{t('privacy.server_logs.item1')}</li>
            <li>{t('privacy.server_logs.item2')}</li>
            <li>{t('privacy.server_logs.item3')}</li>
            <li>{t('privacy.server_logs.item4')}</li>
            <li>{t('privacy.server_logs.item5')}</li>
          </List>
          <Text>{t('privacy.server_logs.legal_basis')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.ssl.heading')}</SectionTitle>
          <Text>{t('privacy.ssl.text')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.contact_form.heading')}</SectionTitle>
          <Text>{t('privacy.contact_form.text1')}</Text>
          <Text>{t('privacy.contact_form.text2')}</Text>
          <Text>{t('privacy.contact_form.text3')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.cookies.heading')}</SectionTitle>
          <Text>{t('privacy.cookies.text1')}</Text>
          <Text>{t('privacy.cookies.text2')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.cookie_manager.heading')}</SectionTitle>
          <Text>{t('privacy.cookie_manager.text1')}</Text>
          <Text>{t('privacy.cookie_manager.text2')}</Text>
          <Text>{t('privacy.cookie_manager.text3')}</Text>
          <ConsentButton type="button" onClick={() => openConsentSettings()}>
            {t('privacy.cookie_manager.button_label')}
          </ConsentButton>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.analytics.heading')}</SectionTitle>
          <Text>{t('privacy.analytics.text1')}</Text>
          <Text>{t('privacy.analytics.text2')}</Text>
          <SubTitle>{t('privacy.analytics.data_heading')}</SubTitle>
          <List>
            <li>{t('privacy.analytics.data_item1')}</li>
            <li>{t('privacy.analytics.data_item2')}</li>
            <li>{t('privacy.analytics.data_item3')}</li>
          </List>
          <Text>{t('privacy.analytics.legal_basis')}</Text>
          <Text>{t('privacy.analytics.opt_out')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.storage_duration.heading')}</SectionTitle>
          <Text>{t('privacy.storage_duration.text')}</Text>
        </Section>

        <Section>
          <SectionTitle>{t('privacy.changes.heading')}</SectionTitle>
          <Text>{t('privacy.changes.text')}</Text>
        </Section>
      </Container>
    </PageContainer>
  );
};

export default Datenschutz;
